import { createSelector } from 'reselect'
import { PAYMENT_OPTIONS, PAYMENT_SCHEDULE_STATUS } from 'constants/payment'
import getDepositAmount from './deposit/getDepositAmount'
import getPaymentScheduleDueToday from './paymentSchedule/getPaymentScheduleDueToday'
import getDynamicPaymentScheduleDueToday from './paymentSchedule/getDynamicPaymentScheduleDueToday'
import getGroupPaymentDueToday from './groupPayment/getGroupPaymentDueToday'
import getCheckoutTotalsView from './getCheckoutTotalsView'
import { getInsuranceTotals } from './insurance'
import { getBookingProtectionTotals } from './bookingProtection'

/**
 * The amount to charge now for the selected payment option: the deposit, the first instalment
 * of a payment schedule or group payment, or the payable total when paying in full.
 */
const getPaymentAmount = createSelector(
  (state: App.State) => state.checkout.payment.paymentSelected,
  getDepositAmount,
  getPaymentScheduleDueToday,
  getDynamicPaymentScheduleDueToday,
  getGroupPaymentDueToday,
  getCheckoutTotalsView,
  (state: App.State) => state.checkout.cart.mode,
  (state: App.State) => state.checkout.cart.existingOrder,
  getInsuranceTotals,
  getBookingProtectionTotals,
  (paymentSelected,
    depositAmount,
    paymentScheduleDueToday,
    dynamicPaymentScheduleDueToday,
    groupPaymentDueToday,
    totals,
    cartMode,
    existingOrder,
    insuranceTotals,
    bookingProtectionTotals,
  ): number => {
    if (paymentSelected === PAYMENT_OPTIONS.DEPOSIT) return depositAmount
    if (paymentSelected === PAYMENT_OPTIONS.PAYMENT_SCHEDULE) return paymentScheduleDueToday
    if (paymentSelected === PAYMENT_OPTIONS.DYNAMIC_PAYMENT_SCHEDULE) return dynamicPaymentScheduleDueToday
    if (paymentSelected === PAYMENT_OPTIONS.GROUP_PAYMENT) return groupPaymentDueToday

    const hasActivePaymentSchedule = existingOrder?.paymentScheduleDetails &&
      existingOrder.paymentScheduleDetails.status === PAYMENT_SCHEDULE_STATUS.ACTIVE

    // We need to replace the price with the payment schedule adjustment since the customer only has to pay a portion of the difference upfront
    if ((cartMode === 'change-dates' || cartMode === 'change-package') && hasActivePaymentSchedule) {
      const paymentScheduleAdjustment = (totals.data.paymentScheduleAdjustment ?? 0) + (insuranceTotals?.data.price ?? 0) + (bookingProtectionTotals?.data.price ?? 0)
      if (paymentScheduleAdjustment) {
        // Subtract credits and gift cards from the payment schedule adjustment amount
        // This mirrors what payableNow does for the regular checkout flow
        const adjustedAmount = paymentScheduleAdjustment -
          (totals.data.creditPaymentAmount ?? 0) -
          (totals.data.giftCardPaymentAmount ?? 0)
        return Math.max(0, adjustedAmount)
      }
    }

    return totals.data.payableNow
  },
)

export default getPaymentAmount
